/**
 * Map durable DiscoverySession → advisor / thin-intake form seeds.
 */

function pick(...vals) {
  for (const v of vals) {
    if (v == null) continue;
    if (typeof v === 'string' && !v.trim()) continue;
    return v;
  }
  return undefined;
}

/** Advisor form_prefill keys from a saved discovery session. */
export function advisorDefaultsFromSession(session) {
  if (!session || typeof session !== 'object') return {};
  const fp = session.formPrefill || {};
  const address = pick(fp.address, fp.location);
  const description = pick(fp.description, fp.proposedUse);
  const propertyType = pick(fp.propertyType, fp.projectType);
  const out = {};
  if (address) {
    out.address = address;
    out.location = address;
  }
  if (description) out.description = description;
  if (fp.proposedUse) out.proposedUse = fp.proposedUse;
  else if (description) out.proposedUse = description;
  if (propertyType) {
    out.propertyType = propertyType;
    out.projectType = propertyType;
  }
  for (const key of [
    'approxSqft', 'servesAlcohol', 'outdoorSeating', 'foodHandling',
    'structuralChange', 'lat', 'lng', 'lon', 'projectKind', 'aduProgram', 'shedSize',
  ]) {
    if (fp[key] != null && fp[key] !== '') out[key] = fp[key];
  }
  return out;
}

/**
 * Thin intake form seed (golden IF-citizen-thin-intake-v1 field names).
 * @param {object|null} session
 * @param {{ sessionId?: string, packageId?: string, applicationTypeId?: string }} [opts]
 */
export function intakeDefaultsFromSession(session, opts = {}) {
  const fp = session?.formPrefill || {};
  const fields = session?.intakeFields || {};
  const sessionId = opts.sessionId || session?.sessionId || '';
  const packageId = opts.packageId || session?.packageId || 'san-francisco';
  const applicationTypeId = opts.applicationTypeId
    || session?.applicationTypeId
    || session?.routing?.applicationTypeIds?.[0]
    || 'building_permit';

  const seed = {
    sessionId,
    packageId,
    applicationTypeId,
  };

  const applicantName = pick(fields.applicantName, fp.applicantName, fp.contactName, fp.name);
  const contactEmail = pick(fields.contactEmail, fp.contactEmail, fp.email);
  const contactPhone = pick(fields.contactPhone, fp.contactPhone, fp.phone);
  const projectAddress = pick(
    fields.projectAddress,
    fields.siteAddressLine1,
    fp.address,
    fp.location,
  );
  const projectDescription = pick(
    fields.projectDescription,
    fields.workDescription,
    fp.description,
    fp.proposedUse,
  );

  if (applicantName) seed.applicantName = applicantName;
  if (contactEmail) seed.contactEmail = contactEmail;
  if (contactPhone) seed.contactPhone = contactPhone;
  if (projectAddress) seed.projectAddress = projectAddress;
  if (projectDescription) seed.projectDescription = projectDescription;

  return seed;
}

/** Structured output slice to rehydrate the advisor outcome tray after return. */
export function advisorRestoredOutputFromSession(session) {
  if (!session?.routing) return null;
  const routing = session.routing;
  const formPrefill = advisorDefaultsFromSession(session);
  const apps = Array.isArray(routing.applicationTypes) ? routing.applicationTypes : [];
  const rows = apps.map((t) => ({
    label: t.label || t.id,
    track: t.track || routing.primaryTrack || '',
    detail: t.typical || t.notes || '',
  }));
  const checklist = {
    title: routing.primaryTrackLabel || 'Applications',
    columns: [
      { id: 'label', label: 'Application' },
      { id: 'track', label: 'Track' },
      { id: 'detail', label: 'Notes' },
    ],
    rows,
    items: apps.map((t) => t.label || t.id).filter(Boolean),
  };
  const summaryLines = [
    routing.primaryTrackLabel
      ? `**Primary path: ${routing.primaryTrackLabel}**`
      : null,
    routing.rationale || null,
  ].filter(Boolean);
  const summaryContent = summaryLines.join('\n\n')
    || 'Restored from your discovery session.';

  return {
    routing,
    fees: session.fees || routing.fees || undefined,
    requirements: session.requirements || routing.requirements || undefined,
    parcel: session.parcel || undefined,
    landUse: session.landUse || session.parcel?.landUse || undefined,
    handoffPacket: session.handoffPacket || undefined,
    table_data: checklist,
    checklist,
    rich_text: { content: summaryContent },
    summary: { content: summaryContent },
    form_prefill: formPrefill,
  };
}
